// The price table: which heat pump sizes are offered (a switch per size) and
// the installed price for each, BEFORE the Boiler Upgrade Scheme grant. The
// grant isn't chosen here: the calculator works it out for each home (£7,500,
// or £9,000 for homes replacing oil or LPG off the gas grid). Used by the Pricing tab and the new-tool wizard's Prices
// step; the parent owns the state ({pricing, text}) and saves it.
//
// `text` keeps each size's price as typed, so half-typed values don't fight the
// number parsing; `pricing` holds the parsed numbers.
import { PRICE_LIMITS, SAMPLE_PRICES, STANDARD_BUS_GRANT, UPLIFT_BUS_GRANT, afterGrant, type WidgetPricing } from "@widget";
import { Toggle } from "./controls";
import "../editor.css";
import "../pricing.css";

export type PriceState = { pricing: WidgetPricing; text: Record<number, string> };

const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;
const inRange = (n: number) => n >= PRICE_LIMITS[0] && n <= PRICE_LIMITS[1];

/** Saved prices as editable text (blank for sizes without a price). */
export function toPriceState(p: WidgetPricing): PriceState {
  return { pricing: p, text: Object.fromEntries(p.steps.map((s) => [s.kw, s.price === null ? "" : String(s.price)])) };
}

/** Why a size can't be saved: switched on without a valid price, or a bad price typed. */
export function rowProblem(state: PriceState, kw: number): string | null {
  const step = state.pricing.steps.find((s) => s.kw === kw)!;
  const t = state.text[kw] ?? "";
  if (t !== "" && (!/^\d+$/.test(t) || !inRange(Number(t)))) return `Enter ${gbp(PRICE_LIMITS[0])} to ${gbp(PRICE_LIMITS[1])}`;
  if (step.enabled && step.price === null) return "Add a price or switch this size off";
  return null;
}

/** How many sizes need attention before the prices can be saved. */
export function priceProblems(state: PriceState): number {
  return state.pricing.steps.filter((s) => rowProblem(state, s.kw) !== null).length;
}

export function PriceEditor({ state, onChange }: { state: PriceState; onChange: (next: PriceState) => void }) {
  const draft = state.pricing;

  function setPrice(kw: number, value: string) {
    // Accept "11,500" or "£11500" as typed; keep digits only
    const digits = value.replace(/[£,\s]/g, "");
    onChange({
      text: { ...state.text, [kw]: digits },
      pricing: { ...draft, steps: draft.steps.map((s) => (s.kw === kw ? { ...s, price: /^\d+$/.test(digits) ? Number(digits) : null } : s)) },
    });
  }

  function setEnabled(kw: number, enabled: boolean) {
    onChange({ ...state, pricing: { ...draft, steps: draft.steps.map((s) => (s.kw === kw ? { ...s, enabled } : s)) } });
  }

  return (
    <>
      <table className="price-table">
        <thead>
          <tr>
            <th scope="col">Offer</th>
            <th scope="col">Heat pump size</th>
            <th scope="col">Your price (before BUS)</th>
            <th scope="col">Homeowner pays (after the {gbp(STANDARD_BUS_GRANT)} grant)</th>
          </tr>
        </thead>
        <tbody>
          {draft.steps.map((s) => {
            const t = state.text[s.kw] ?? "";
            const problem = rowProblem(state, s.kw);
            return (
              <tr key={s.kw} className={s.enabled ? "" : "is-off"}>
                <td className="offer-cell">
                  <Toggle id={`offer-${s.kw}`} checked={s.enabled} ariaLabel={`Offer ${s.kw} kW`} onChange={(v) => setEnabled(s.kw, v)} />
                </td>
                <th scope="row">{s.kw} kW</th>
                <td>
                  <div className={`price-input${problem ? " is-bad" : ""}`}>
                    <span aria-hidden="true">£</span>
                    <input
                      inputMode="numeric"
                      aria-label={`Price for ${s.kw} kW, before BUS`}
                      placeholder={SAMPLE_PRICES[s.kw].toLocaleString("en-GB")}
                      value={t === "" ? "" : /^\d+$/.test(t) ? Number(t).toLocaleString("en-GB") : t}
                      disabled={!s.enabled}
                      onChange={(e) => setPrice(s.kw, e.target.value)}
                    />
                  </div>
                  {problem && <span className="price-error">{problem}</span>}
                </td>
                <td className="after-bus">
                  {!s.enabled ? (
                    <span className="muted">Not offered</span>
                  ) : s.price !== null ? (
                    gbp(afterGrant(s.price, STANDARD_BUS_GRANT))
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* The grant is worked out per home, not chosen here */}
      <p className="price-note">
        The Boiler Upgrade Scheme grant is worked out for each home: {gbp(STANDARD_BUS_GRANT)} as standard, or {gbp(UPLIFT_BUS_GRANT)} for homes
        replacing oil or LPG heating off the gas grid (until 31 March 2027).
      </p>
    </>
  );
}
